import { useParams, Link, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ArrowLeft, ArrowRight, Check, Phone, Mail, Globe, Palette, Users, ShoppingCart, FileText, Code, Bot, Search } from 'lucide-react'
import { services } from '../data/services'
import { useEffect, useRef } from 'react'
import gsap from 'gsap'

const serviceIcons: Record<string, React.ElementType> = {
  'web-dev': Globe,
  'branding': Palette,
  'crm': Users,
  'ecommerce': ShoppingCart,
  'landing': FileText,
  'saas': Code,
  'ai': Bot,
  'seo': Search,
}

const serviceHeroImages: Record<string, string> = {
  'web-dev': '/images/service-webdev.jpg',
  'branding': '/images/service-branding.jpg',
  'crm': '/images/service-crm.jpg',
  'ecommerce': '/images/service-ecommerce.jpg',
  'landing': '/images/service-landing.jpg',
  'saas': '/images/service-saas.jpg',
  'ai': '/images/service-ai.jpg',
  'seo': '/images/service-seo.jpg',
}

const serviceHeroVideos: Record<string, string> = {
  'web-dev': '/images/web-development.mp4',
  'branding': '/images/branding-design.mp4',
  'crm': '/images/crm.mp4',
  'ecommerce': '/images/commerce.mp4',
  'ai': '/images/ai-automation.mp4',
  'seo': '/images/seo.mp4',
  'landing': '/images/landing.mp4',
  'saas': '/images/saas.mp4'


}

const serviceFaqs: Record<string, { q: string; a: string }[]> = {
  'web-dev': [
    { q: 'How long does it take to build a custom website?', a: 'A typical corporate website takes 4-8 weeks from kickoff to launch. Complex web applications with custom features may take 8-16 weeks. We provide a detailed timeline during our initial consultation.' },
    { q: 'Do you build responsive websites that work on mobile?', a: 'Absolutely. Every website we build is mobile-first responsive, tested on 15+ devices and screen sizes. We ensure your site looks great and performs well on phones, tablets, and desktops.' },
    { q: 'What technologies do you use for web development?', a: 'We primarily use React, Next.js, Vue.js, and Laravel depending on your project needs. For content-driven sites, we also work with WordPress, Strapi, and Sanity CMS.' },
    { q: 'Will my website be SEO-optimized?', a: 'Yes. We implement technical SEO best practices including semantic HTML, structured data, fast load times (90+ PageSpeed), proper meta tags, and XML sitemaps. We also offer ongoing SEO services.' },
  ],
  'branding': [
    { q: 'What does a complete branding package include?', a: 'Our full branding package includes brand strategy, logo design, color palette, typography system, brand guidelines document, business card design, social media templates, and a brand presentation deck.' },
    { q: 'How long does the branding process take?', a: 'A complete brand identity project typically takes 3-6 weeks. This includes discovery, concept development, revisions, and final delivery of all brand assets.' },
    { q: 'Can you redesign an existing brand?', a: 'Yes, we specialize in brand refreshes and redesigns. We analyze your current brand equity, identify what works and what does not, and evolve your identity while maintaining recognition.' },
    { q: 'Do you handle both digital and print branding?', a: 'Yes. We design for all touchpoints — website, social media, packaging, print collateral, signage, and more. Our brand guidelines cover both digital and print applications.' },
  ],
  'crm': [
    { q: 'Can you integrate with our existing CRM like Salesforce or HubSpot?', a: 'Yes. We build custom integrations with Salesforce, HubSpot, Zoho, Pipedrive, and other popular CRMs. We can also build a custom CRM from scratch tailored to your exact workflow.' },
    { q: 'How long does it take to build a custom CRM?', a: 'A custom CRM typically takes 3-5 months depending on complexity. We start with an MVP that covers your core needs, then iterate based on user feedback.' },
    { q: 'Will our team need training?', a: 'We provide comprehensive training sessions and documentation. Most teams are fully comfortable with the new system within 1-2 weeks of launch.' },
    { q: 'Is our data secure?', a: 'Security is our top priority. We implement enterprise-grade security including encryption at rest and in transit, role-based access control, audit logs, and regular security assessments.' },
  ],
  'ecommerce': [
    { q: 'Which e-commerce platform do you recommend?', a: 'We recommend Shopify for most businesses due to its robust ecosystem. For highly custom needs, we build headless e-commerce solutions with custom backends. We also work with WooCommerce for WordPress-based stores.' },
    { q: 'Can you help migrate from our existing store?', a: 'Yes. We have experience migrating from Magento, WooCommerce, BigCommerce, and custom platforms to Shopify or headless solutions. We ensure zero data loss and minimal downtime.' },
    { q: 'Do you handle payment gateway integration?', a: 'We integrate all major payment processors including Stripe, PayPal, Square, and Authorize.net. We also support buy-now-pay-later options like Klarna and Affirm.' },
    { q: 'Can you optimize our store for conversions?', a: 'Absolutely. We implement CRO best practices including optimized checkout flows, abandoned cart recovery, product recommendations, A/B testing, and performance optimization.' },
  ],
  'landing': [
    { q: 'What makes a high-converting landing page?', a: 'Clear value proposition, compelling headline, social proof, single focus CTA, fast load time, mobile optimization, and trust signals. We design every element to drive action.' },
    { q: 'Do you offer A/B testing?', a: 'Yes. We build landing pages with A/B testing architecture built-in. We can also manage ongoing testing and optimization to continuously improve conversion rates.' },
    { q: 'How quickly can you build a landing page?', a: 'A custom landing page typically takes 1-2 weeks from concept to launch. For urgent campaigns, we can deliver in as little as 5 business days.' },
    { q: 'Can you integrate with our marketing tools?', a: 'We integrate with all major marketing platforms including Google Analytics, Facebook Pixel, HubSpot, Mailchimp, Salesforce, and any other tools in your marketing stack.' },
  ],
  'saas': [
    { q: 'How do you approach SaaS MVP development?', a: 'We follow a lean approach — identify core features, build a working MVP in 8-12 weeks, launch to early users, and iterate based on feedback. This minimizes risk and gets you to market faster.' },
    { q: 'Can you handle the technical architecture?', a: 'Yes. We design scalable multi-tenant architectures using microservices, serverless functions, and cloud infrastructure. Every decision is made with scale and security in mind.' },
    { q: 'Do you help with subscription billing?', a: 'We integrate Stripe Billing, Chargebee, or Paddle for subscription management, including tiered pricing, trials, coupons, and automated invoicing.' },
    { q: 'What about ongoing maintenance and support?', a: 'We offer flexible maintenance packages including bug fixes, security updates, feature enhancements, and 24/7 monitoring. Most SaaS clients choose our monthly retainer.' },
  ],
  'seo': [
    { q: 'How long does it take to see SEO results?', a: 'SEO is a long-term investment. Most clients begin seeing meaningful improvements in rankings and organic traffic within 3-6 months. Technical fixes and on-page optimization can show early wins in the first 4-8 weeks.' },
    { q: 'Do you guarantee first-page rankings?', a: 'No ethical agency can guarantee specific rankings — search algorithms change constantly. What we guarantee is a data-driven strategy, transparent reporting, and consistent month-over-month improvement following Google best practices.' },
    { q: 'What is included in your monthly SEO service?', a: 'Each month we deliver technical monitoring, content creation, link building, keyword tracking, competitor analysis, and a detailed performance report with clear next steps.' },
    { q: 'Can you help with local SEO for my business?', a: 'Yes. We optimize your Google Business Profile, build local citations, manage reviews, and create location-targeted content to help you dominate local search results in your service area.' },
  ],
}

export default function ServiceDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const sectionRef = useRef<HTMLDivElement>(null)
  const service = services.find(s => s.id === id)

  useEffect(() => {
    if (!service) return
    window.scrollTo(0, 0)
    const ctx = gsap.context(() => {
      const elements = sectionRef.current?.querySelectorAll('.sd-animate')
      if (elements && elements.length > 0) {
        gsap.fromTo(elements, { y: 40, opacity: 0 }, {
          y: 0, opacity: 1, duration: 0.7, stagger: 0.06, ease: 'power3.out', delay: 0.2
        })
      }
    }, sectionRef)
    return () => ctx.revert()
  }, [service, id])

  if (!service) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Helmet>
          <title>Service Not Found — Technifire Software</title>
          <meta name="description" content="The requested service could not be found. Browse all services by Technifire Software." />
        </Helmet>
        <div className="text-center">
          <h1 className="text-2xl text-white mb-4">Service not found</h1>
          <Link to="/services" className="btn-primary">Back to Services</Link>
        </div>
      </div>
    )
  }

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.title,
    "description": service.description,
    "provider": { "@type": "Organization", "name": "Technifire Software", "url": "https://technifire.io" },
    "url": `https://technifire.io/services/${id}`
  }

  const Icon = serviceIcons[service.id] || Globe
  const heroImage = serviceHeroImages[service.id] || '/images/service-webdev.jpg'
  const heroVideo = serviceHeroVideos[service.id]
  const faqs = serviceFaqs[service.id] || []
  const otherServices = services.filter(s => s.id !== id).slice(0, 3)

  return (
    <div className="min-h-screen bg-[#050505]" ref={sectionRef}>
      <Helmet>
        <title>{service.title} - Technifire Software</title>
        <meta name="description" content={service.description.slice(0, 160)} />
        <link rel="canonical" href={`https://technifire.io/services/${id}`} />
        <meta property="og:title" content={`${service.title} — Technifire Software`} />
        <meta property="og:description" content={service.description.slice(0, 160)} />
        <meta property="og:url" content={`https://technifire.io/services/${id}`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>
      {/* Hero */}
      <div className="relative h-[50vh] lg:h-[60vh] overflow-hidden">
        {heroVideo ? (
          <video src={heroVideo} autoPlay loop muted playsInline className="w-full h-full object-cover" />
        ) : (
          <img src={heroImage} alt={service.title} className="w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/70 to-transparent" />

        <button onClick={() => navigate(-1)} className="absolute top-24 left-6 lg:left-10 flex items-center gap-2 text-white/70 hover:text-white transition-colors z-10">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back</span>
        </button>

        <div className="absolute bottom-0 left-0 right-0 px-6 lg:px-10 pb-10">
          <div className="sd-animate flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
              <Icon className="w-5 h-5 text-white" />
            </div>
            <span className="text-mono text-[#9A9A9A]">SERVICE {service.number}</span>
          </div>
          <h1 className="sd-animate text-h2 uppercase text-white max-w-3xl">{service.title}</h1>
        </div>
      </div>

      {/* Description */}
      <div className="px-6 lg:px-10 py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 sd-animate">
            <p className="text-xl lg:text-2xl text-white/80 leading-relaxed">{service.description}</p>
          </div>
          <div className="sd-animate">
            <Link to="/contacts" className="btn-primary w-full justify-center">
              BOOK A STRATEGY CALL
              <ArrowRight className="w-4 h-4" />
            </Link>
            <div className="mt-4 flex items-center justify-center gap-4 text-sm text-[#9A9A9A]">
              <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> +1 (425) 541-4001</span>
            </div>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="px-6 lg:px-10 py-16 border-y border-white/5">
        <span className="text-mono text-[#9A9A9A] mb-3 block sd-animate">WHAT YOU GET</span>
        <h2 className="text-h3 text-white mb-10 sd-animate">Everything included</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {service.features.map((feature, i) => (
            <div key={i} className="sd-animate flex items-start gap-3  bg-gradient-to-r from-[#B7000E] via-[#b7000f94] to-[#b7000f5f] rounded-xl p-5">
              <Check className="w-5 h-5 text-[#4ADE80] shrink-0 mt-0.5" />
              <span className="text-white/80 text-sm">{feature}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Process */}
      <div className="px-6 lg:px-10 py-16">
        <span className="text-mono text-[#9A9A9A] mb-3 block sd-animate">OUR PROCESS</span>
        <h2 className="text-h3 text-white mb-10 sd-animate">How it works</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6">
          {[
            { step: '01', title: 'Discover', desc: 'Business goals, users, workflows, existing systems and measurable success criteria.' },
            { step: '02', title: 'Plan', desc: 'Scope, architecture, timeline, milestones, responsibilities and risk controls.' },
            { step: '03', title: 'Prototype', desc: 'Validate the experience, workflow or conversation before full development.' },
            { step: '04', title: 'Build', desc: 'Iterative development and integrations with regular demonstrations.' },
            { step: '05', title: 'Test', desc: 'Functional, usability, performance and acceptance testing before release.' },
            { step: '06', title: 'Launch & Improve', desc: 'Deployment, training, monitoring, support and continuous optimization.' },
          ].map((phase, i) => (
            <div key={i} className="sd-animate relative">
              <span className="text-5xl font-light text-white/10 mb-3 block">{phase.step}</span>
              <h4 className="text-white font-medium mb-2">{phase.title}</h4>
              <p className="text-sm text-[#9A9A9A]">{phase.desc}</p>
              {i < 5 && <div className="hidden lg:block absolute top-8 right-0 w-full h-px bg-white/10" style={{ marginRight: '-1.5rem' }} />}
            </div>
          ))}
        </div>
      </div>

      {/* Technologies */}
      <div className="px-6 lg:px-10 py-10 border-y border-white/5">
        <span className="text-mono text-[#9A9A9A] mb-4 block sd-animate">TECHNOLOGIES</span>
        <div className="flex flex-wrap gap-2 sd-animate">
          {service.technologies.map((tech) => (
            <span key={tech} className="px-4 py-2 bg-white/5 border border-white/10 rounded-full text-sm text-white/70">{tech}</span>
          ))}
        </div>
      </div>

      {service.id === 'ai' && (
        <section className="px-6 lg:px-10 py-16 border-b border-white/5">
          <span className="text-mono text-[#E30614] mb-3 block">AI SOLUTION TYPES</span>
          <h2 className="text-h3 text-white mb-4">More than a greeting chatbot</h2>
          <p className="max-w-3xl text-white/60 leading-relaxed mb-10">We design practical agents around approved knowledge, defined actions, integrations and human escalation—not open-ended automation without controls.</p>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[
              ['Website AI Chatbots','Answer FAQs, qualify enquiries, recommend services, collect contact data and book appointments.'],
              ['AI Voice Agents','Handle inbound or outbound conversations, route calls, update systems and transfer to people when required.'],
              ['WhatsApp Automation','Support enquiries, lead qualification, order updates, booking and contextual human handoff.'],
              ['Internal AI Assistants','Search approved business knowledge, summarize information and support controlled staff workflows.']
            ].map(([title,text]) => <article key={title} className="rounded-2xl border border-white/10 bg-[#0A0A0A] p-6"><h3 className="text-xl font-light">{title}</h3><p className="mt-3 text-sm leading-relaxed text-white/55">{text}</p></article>)}
          </div>
          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            <div className="rounded-2xl border border-white/10 p-7"><h3 className="text-2xl font-light">Responsible implementation</h3><div className="mt-5 grid gap-3 sm:grid-cols-2">{['Controlled knowledge sources','Human escalation rules','Conversation monitoring','Role and access planning','Testing before production','Data retention configuration'].map(x=><div key={x} className="flex gap-2 text-sm text-white/65"><Check className="h-4 w-4 shrink-0 text-green-400"/>{x}</div>)}</div></div>
            <div className="rounded-2xl border border-white/10 p-7"><h3 className="text-2xl font-light">Common integrations</h3><p className="mt-3 text-sm leading-relaxed text-white/55">HubSpot, Salesforce, Zoho, Slack, Microsoft Teams, Google Workspace, Shopify, WooCommerce, Twilio, WhatsApp Business, Calendly, Stripe, Zapier, Make and custom REST APIs—subject to API access.</p><Link to="/solutions/ai-automation-agency" className="mt-6 inline-flex items-center gap-2 text-sm font-medium">EXPLORE AI AUTOMATION OFFER <ArrowRight className="h-4 w-4"/></Link></div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {faqs.length > 0 && (
        <div className="px-6 lg:px-10 py-16">
          <span className="text-mono text-[#9A9A9A] mb-3 block sd-animate">FAQ</span>
          <h2 className="text-h3 text-white mb-8 sd-animate">Common questions</h2>
          <div className="space-y-0 max-w-3xl">
            {faqs.map((faq, i) => (
              <div key={i} className="sd-animate border-t border-white/10 py-5">
                <h4 className="text-white font-medium mb-2">{faq.q}</h4>
                <p className="text-sm text-[#9A9A9A] leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="px-6 lg:px-10 py-16">
        <div className="sd-animate  bg-gradient-to-r from-[#B7000E] via-[#b7000f94] to-[#b7000f5f] rounded-3xl p-8 lg:p-12 text-center">
          <h2 className="text-h3 text-white mb-3">Ready to get started?</h2>
          <p className="text-[#9A9A9A] max-w-lg mx-auto mb-8">
            Tell us about your project and we will get back to you within 24 hours with a free consultation and detailed proposal.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/contacts" className="btn-primary">
              <Mail className="w-4 h-4" />
              START YOUR PROJECT
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="tel:+14255414001" className="btn-outline">
              <Phone className="w-4 h-4" />
              CALL US
            </a>
          </div>
        </div>
      </div>

      {/* Other Services */}
      <div className="px-6 lg:px-10 pb-20">
        <span className="text-mono text-[#9A9A9A] mb-4 block">EXPLORE MORE</span>
        <h3 className="text-xl text-white mb-6">Other services</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {otherServices.map((os) => {
            const OsIcon = serviceIcons[os.id] || Globe
            return (
              <Link key={os.id} to={`/services/${os.id}`} className="group bg-[#0a0a0a] rounded-2xl p-6 border border-white/5 hover:border-white/15 transition-all">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-white/10 transition-colors">
                    <OsIcon className="w-4 h-4 text-[#9A9A9A] group-hover:text-white transition-colors" />
                  </div>
                  <span className="text-mono text-[#9A9A9A]">{os.number}</span>
                </div>
                <h4 className="text-white font-medium group-hover:text-[#9A9A9A] transition-colors">{os.title}</h4>
              </Link>
            )
          })}
        </div>
      </div>
    </div>
  )
}
